import { Number } from 'stdlib/Number'
import { is }     from 'stdlib/is'

import MatchMedia from '../MatchMedia/MatchMedia'

import globalEvents from '../globalEvents/globalEvents'

import halfPx           from './utils/halfPx'
import getScaleMode     from './utils/getScaleMode'
import getScaleRatio    from './utils/getScaleRatio'
import getElementOffset from './utils/getElementOffset'
import BcrCalibration   from './utils/BcrCalibration'

// Ezt veszi fix méretnek, és ezt fogja scalelni
export const WANTED_W = 1920
export const WANTED_H = 1080

export const CONTENT_SCALE_EVENT = 'ContentScale:Done'

interface IDimensions {
  readonly wantedW: number
  readonly wantedH: number
}

interface IResponsiveDimensions {
  readonly portrait: IDimensions
  readonly landscape: IDimensions
}

let isAlreadyInitialized = false

type TScaleMode = 'translate' | 'zoom'

export default class ContentScale {
  public static getScaleRatio = getScaleRatio
  public static getElementOffset = getElementOffset
  public static getScaleMode = getScaleMode

  private static wantedW = WANTED_W
  private static wantedH = WANTED_H

  private static mode: TScaleMode = 'translate'

  public static get scaleMode (): TScaleMode {
    return ContentScale.mode
  }

  /**
   * ScaleMode állítása.
   * @param mode - Scale mód.
   */
  public static setScaleMode (mode: TScaleMode): void {
    ContentScale.mode = mode
  }

  /**
   * Reszponzív scalezés.
   * @param dimensions - Portrait és landscape nézethez beállítások.
   */
  public static responsive (dimensions: IResponsiveDimensions): void {
    const mql = MatchMedia.create('(orientation: portrait)')

    ContentScale.setByMatchMedia(mql.isMatch(), dimensions)

    mql.addEventListener('change', () => {
      ContentScale.setByMatchMedia(mql.isMatch(), dimensions)
    })
  }

  /**
   * Az szélesség és a magasság beállítása.
   * @param wantedW - Szélesség.
   * @param wantedH - Magasság.
   */
  public static setDimensions (wantedW: number, wantedH: number): void {
    ContentScale.wantedW = wantedW
    ContentScale.wantedH = wantedH
  }

  /**
   * Scalezés indítása.
   * @param width  - A szélesség, amihez aránytartók akarunk lenni.
   * @param height - A magasság, amihez aránytartók akarunk lenni.
   */
  public static scale (width: number = window.innerWidth, height: number = window.innerHeight): void {
    const root = document.querySelector('#root')

    if (!is.instanceOf(root, HTMLElement)) {
      return
    }

    root.style.position = 'relative'

    root.style.removeProperty('zoom')
    root.style.removeProperty('transform')
    root.style.removeProperty('transform-origin')

    // Ha a natív dimenziók megegyeznek az általunk megadottal, akkor nem kell scale, csak kifeszítés
    if (width === ContentScale.wantedW && height === ContentScale.wantedH) {
      root.style.top    = '0'
      root.style.left   = '0'
      root.style.width  = '100%'
      root.style.height = '100%'
    }
    // Nem egyezik meg a megadottal: számolni kell scale-t
    else {
      const ratio = Number.parse(getScaleRatio(width, height, ContentScale.wantedW, ContentScale.wantedH))

      root.style.height = `${ ContentScale.wantedH }px`
      root.style.width  = `${ ContentScale.wantedW }px`

      // A getBoundingClientRect helyett kézzel számoljuk ki az új méreteket
      const scaledHeight = ratio * ContentScale.wantedH
      const scaledWidth  = ratio * ContentScale.wantedW

      if (ContentScale.mode === 'translate') {
        root.style.transformOrigin = '0px 0px'

        // A sima scale jó a scale3d helyett, mert azzal szőrösödött a kép.
        // Mind a kettőt ugyanúgy a GPU hajtja végre, így nincs teljesítménybeli különbség.
        // Ráadásul a scale3d több erőforrást is igényel.
        root.style.transform = `translate3d(0,0,0) scale(${ ratio },${ ratio })`

        // Középre mozgatás
        root.style.top  = halfPx(height, scaledHeight)
        root.style.left = halfPx(width, scaledWidth)
      }
      else {
        /**
         * Átmeneti HACKY megoldás.
         * /* Az Android-WebView probléma miatt, lásd:
         * {@link https://balazs-diak.monday.com/boards/1255080304/pulses/2093820836.}.
         */
        root.style.zoom = `${ ratio }`

        // Középre mozgatás
        // A css transformhoz képest a zoomnál az eltolódás értékét
        // vissza kell szorozni a zoom „arányával”.
        root.style.top  = `${ (Number.half(height - scaledHeight) * (1 / ratio)) }px`
        root.style.left = `${ (Number.half(width - scaledWidth) * (1 / ratio)) }px`
      }
    }

    BcrCalibration.calibrate()

    const ev = new Event(CONTENT_SCALE_EVENT)
    // oxlint-disable-next-line eslint-js/no-restricted-properties -- Csak ezért ne húzzuk be a DOM-ot.
    document.dispatchEvent(ev)
  }

  /** Az aktuális szélesség és magasság, amihez aránytartóak vagyunk. */
  public static getDimensions (): IDimensions {
    return { wantedW: ContentScale.wantedW, wantedH: ContentScale.wantedH }
  }

  /**
   * Inicializálás.
   * @param mode - Miképpen valósítsa meg a scalelést.
   */
  public static init (mode: TScaleMode): void {
    if (isAlreadyInitialized) {
      return
    }

    ContentScale.mode = mode

    globalEvents.onReady((): void => ContentScale.scale())
    globalEvents.onResize((): void => ContentScale.scale())
    globalEvents.onOrientationChange((): void => ContentScale.scale())

    isAlreadyInitialized = true
  }

  /**
   * Az adott orientációhoz beállítjuk a szélességet és magasságot.
   * @param matches    - Match-el az orientációhoz?
   * @param dimensions - A dimenziókat tároló object.
   */
  private static setByMatchMedia (matches: boolean, dimensions: IResponsiveDimensions): void {
    const orientation = matches ? dimensions.portrait : dimensions.landscape
    ContentScale.setDimensions(orientation.wantedW, orientation.wantedH)
  }
}
